import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Square, Mic } from 'lucide-react';

interface RecordingBarProps {
  recordingText: string;
  onStop: () => void;
}

const RecordingBar: React.FC<RecordingBarProps> = ({ recordingText, onStop }) => {
  const textRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of text
  useEffect(() => {
    if (textRef.current) {
      textRef.current.scrollTop = textRef.current.scrollHeight;
    }
  }, [recordingText]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
      }}
      className="fixed bottom-8 left-0 right-0 z-50 flex justify-center px-8"
    >
      <motion.div
        className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden w-full max-w-3xl"
        style={{
          boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.03)',
        }}
        animate={{
          scale: [1, 1.01, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="flex items-stretch">
          {/* Recording Status Indicator */}
          <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-red-100 border-r border-red-200/50 flex items-center space-x-3">
            <motion.div
              className="relative"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="absolute inset-0 bg-red-500 rounded-full blur-md opacity-50" />
              <div className="relative w-3 h-3 bg-red-500 rounded-full" />
            </motion.div>
            
            <div className="flex items-center space-x-2">
              <Mic className="w-4 h-4 text-red-600" strokeWidth={2} />
              <span className="text-sm font-medium text-red-700">Recording</span>
            </div>

            {/* Audio Wave Animation */}
            <motion.div className="flex items-center space-x-1">
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1 bg-red-400 rounded-full"
                  animate={{
                    height: ['12px', '20px', '12px'],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.1,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </motion.div>
          </div>

          {/* Streaming Text Display - 2 lines with scroll */}
          <div className="flex-1 px-6 py-3 flex items-center">
            <div className="flex-1 relative">
              <div 
                ref={textRef}
                className="overflow-auto scrollbar-hide h-12"
                style={{
                  scrollBehavior: 'smooth',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <motion.div
                  className="text-gray-700 text-sm leading-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {recordingText}
                  <motion.span
                    className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 align-middle"
                    animate={{
                      opacity: [1, 0, 1],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.div>
              </div>

              {/* Fade edges for overflow */}
              <div className="absolute left-0 top-0 right-0 h-3 bg-gradient-to-b from-white to-transparent pointer-events-none" />
              <div className="absolute left-0 bottom-0 right-0 h-3 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </div>
          </div>

          {/* Stop Button */}
          <div className="px-4 py-4 border-l border-gray-100">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onStop}
              className="w-12 h-12 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 
                       flex items-center justify-center text-white shadow-lg transition-all"
            >
              <Square className="w-5 h-5" fill="currentColor" strokeWidth={0} />
            </motion.button>
          </div>
        </div>

        {/* Recording Progress Bar */}
        <div className="h-1 bg-gray-100 relative overflow-hidden">
          <motion.div
            className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-red-400 via-red-500 to-red-400"
            animate={{
              x: ['-100%', '200%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{ width: '50%' }}
          />
        </div>
      </motion.div>

      {/* Subtle Glow Effect */}
      <motion.div
        className="absolute inset-0 rounded-3xl bg-gradient-to-r from-red-400/10 via-orange-400/10 to-red-400/10 blur-3xl -z-10"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Style for hiding scrollbar */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `
      }} />
    </motion.div>
  );
};

export default RecordingBar;